import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ApiService from './services/ApiService';
import LanguageSwitcher from './components/LanguageSwitcher';
import { usePageMetadataWithRoute } from './hooks/usePageMetadata';

function RankedElectionCreate() {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation(['election', 'common', 'validation']);
    
    // Page metadata
    const { title, description } = usePageMetadataWithRoute('/create-election');
    
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        isProtected: true,
        adminOnlyOptionModifications: true
    });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [validationErrors, setValidationErrors] = useState({});

    const validateForm = () => {
        const errors = {};
        
        if (!formData.title.trim()) {
            errors.title = t('validation:required', { field: t('validation:fields.title') });
        } else if (formData.title.trim().length < 3) {
            errors.title = t('validation:minLength', { field: t('validation:fields.title'), min: 3 });
        }
        
        if (formData.description && formData.description.length > 500) {
            errors.description = t('validation:maxLength', { field: t('validation:fields.description'), max: 500 });
        }
        
        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }
        
        setIsLoading(true);
        setError('');
        
        try {
            const data = await ApiService.createElection(formData);
            navigate(`/ranked-elections/${data.id}`);
        } catch (err) {
            setError(err.message || t('validation:errors.failedToCreate', { item: t('validation:items.election') }));
            setIsLoading(false);
        }
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        
        // Clear validation error when user starts typing
        if (validationErrors[field]) {
            setValidationErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };

    // Check if current language is RTL (Arabic)
    const isRTL = i18n.language === 'ar';

    return (
        <>
            <title>{title}</title>
            <meta name="description" content={description} />
            <div className={`create-election-container ${isRTL ? 'rtl' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
            <div className="language-switcher-container position-fixed top-0 end-0 m-3" style={{ zIndex: 1000 }}>
                <LanguageSwitcher />
            </div>
            
            <div className="container-fluid h-100">
                <div className="row h-100 justify-content-center align-items-center">
                    <div className="col-12 col-md-8 col-lg-6">
                        {/* Navigation */}
                        <div className="create-election-nav mb-4">
                            <Link to="/" className="btn btn-outline-light btn-sm">
                                {t('common:navigation.backToHome')}
                            </Link>
                        </div>
                        
                        {/* Form Card */}
                        <div className="create-election-card">
                            <div className="create-election-header">
                                <h1 className="create-election-title">{t('election:create.title')}</h1>
                                <p className="create-election-subtitle">
                                    {t('election:create.tagline')}{' '}
                                    <Link to="/privacy" className="link-in-text">
                                        {t('election:create.privacyLink')}
                                    </Link>
                                </p>
                            </div>
                            
                            <form onSubmit={handleSubmit} className="create-election-form">
                                {error && (
                                    <div className="alert alert-danger" role="alert">
                                        {error}
                                    </div>
                                )}
                                
                                <div className="mb-4">
                                    <label htmlFor="title" className="form-label">
                                        {t('election:create.titleField')} <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        id="title"
                                        className={`form-control form-control-lg ${validationErrors.title ? 'is-invalid' : ''}`}
                                        placeholder={t('common:forms.placeholder.enterTitle')}
                                        value={formData.title}
                                        onChange={(e) => handleInputChange('title', e.target.value)}
                                        disabled={isLoading}
                                        maxLength={100}
                                    />
                                    {validationErrors.title && (
                                        <div className="invalid-feedback">
                                            {validationErrors.title}
                                        </div>
                                    )}
                                </div>
                                
                                <div className="mb-4">
                                    <label htmlFor="description" className="form-label">
                                        {t('election:create.descriptionField')}
                                    </label>
                                    <textarea
                                        id="description"
                                        className={`form-control ${validationErrors.description ? 'is-invalid' : ''}`}
                                        rows="3"
                                        placeholder={t('common:forms.placeholder.enterDescription')}
                                        value={formData.description}
                                        onChange={(e) => handleInputChange('description', e.target.value)}
                                        disabled={isLoading}
                                        maxLength={500}
                                    />
                                    {validationErrors.description && (
                                        <div className="invalid-feedback">
                                            {validationErrors.description}
                                        </div>
                                    )}
                                    <div className="form-text">
                                        {t('common:forms.characterCount', { count: formData.description.length, max: 500 })}
                                    </div>
                                </div>

                                
                                <div className="create-election-actions">
                                    <button
                                        type="submit"
                                        className="btn btn-success btn-lg create-election-submit"
                                        disabled={isLoading}
                                    >
                                        {isLoading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                {t('election:create.creating')}
                                            </>
                                        ) : (
                                            t('election:create.createButton')
                                        )}
                                    </button>
                                    
                                    <Link
                                        to="/"
                                        className="btn btn-outline-secondary btn-lg"
                                        disabled={isLoading}
                                    >
                                        {t('common:buttons.cancel')}
                                    </Link>
                                </div>
                                
                                <div className="mt-3 text-center">
                                    <small className="text-muted">
                                        {t('election:create.helpText')}
                                    </small>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
            </div>
        </>
    );
}

export default RankedElectionCreate;